import { ethers } from 'ethers';

export const formatAddress = (address, start = 6, end = 4) => {
  if (!address) return '';
  return `${address.slice(0, start)}...${address.slice(-end)}`;
};

export const formatNumber = (num, decimals = 2) => {
  if (!num) return '0';
  const n = parseFloat(num);
  if (n >= 1000000) return (n / 1000000).toFixed(decimals) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(decimals) + 'K';
  return n.toFixed(decimals);
};

// Format small numbers with 4 decimal places (for level income, small amounts)
export const formatSmallNumber = (num) => {
  if (!num) return '0.0000';
  const n = parseFloat(num);
  if (n === 0) return '0.0000';
  if (n >= 1000000) return (n / 1000000).toFixed(4) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(4) + 'K';
  if (n >= 1) return n.toFixed(4);
  // For very small numbers, show up to 4 decimal places
  return n.toFixed(4);
};

// Smart format - uses 4 decimals for small values, 2 for larger
export const formatSmart = (num) => {
  if (!num) return '0.00';
  const n = parseFloat(num);
  if (n === 0) return '0.00';
  // If value is less than 0.01, show 4 decimals
  if (n > 0 && n < 0.01) return n.toFixed(4);
  // Otherwise show 2 decimals
  if (n >= 1000000) return (n / 1000000).toFixed(2) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(2) + 'K';
  return n.toFixed(2);
};

export const formatUSDT = (amount) => {
  if (!amount) return '0.00';
  try {
    const formatted = ethers.utils.formatUnits(amount, 18);
    return parseFloat(formatted).toFixed(2);
  } catch {
    return '0.00';
  }
};

// Format USDT with 4 decimals for small amounts
export const formatUSDTSmall = (amount) => {
  if (!amount) return '0.0000';
  try {
    const formatted = ethers.utils.formatUnits(amount, 18);
    const n = parseFloat(formatted);
    if (n > 0 && n < 0.01) return n.toFixed(4);
    return n.toFixed(2);
  } catch {
    return '0.0000';
  }
};

export const parseUSDT = (amount) => {
  try {
    return ethers.utils.parseUnits(amount.toString(), 18);
  } catch {
    return ethers.BigNumber.from(0);
  }
};

export const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(Number(timestamp) * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const timeAgo = (timestamp) => {
  if (!timestamp) return '';
  const seconds = Math.floor(Date.now() / 1000 - Number(timestamp));
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return true;
  }
};

export const generateReferralLink = (address) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/?ref=${address}`;
};

export const getReferralFromUrl = () => {
  try {
    let ref = null;
    
    // Try regular query params first
    let urlParams = new URLSearchParams(window.location.search);
    ref = urlParams.get('ref');
    console.log('getReferralFromUrl - search:', window.location.search, 'ref:', ref);
    
    // If not found, try hash-based URL (e.g., /#/?ref=...)
    if (!ref && window.location.hash) {
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        urlParams = new URLSearchParams(hashParts[1]);
        ref = urlParams.get('ref');
        console.log('getReferralFromUrl - hash params ref:', ref);
      }
    }
    
    // Try to get from full URL using regex
    if (!ref) {
      const fullUrl = window.location.href;
      const refMatch = fullUrl.match(/[?&]ref=([^&#]+)/);
      if (refMatch) {
        ref = refMatch[1];
        console.log('getReferralFromUrl - regex match ref:', ref);
      }
    }
    
    // Finally, check sessionStorage (set during redirect)
    if (!ref) {
      ref = sessionStorage.getItem('referralAddress');
      if (ref) {
        console.log('getReferralFromUrl - from sessionStorage:', ref);
      }
    }
    
    // Store in sessionStorage for future use
    if (ref) {
      sessionStorage.setItem('referralAddress', ref);
    }
    
    return ref || '';
  } catch (e) {
    console.error('Error getting referral from URL:', e);
    return '';
  }
};

export const isValidAddress = (address) => {
  try {
    return ethers.utils.isAddress(address);
  } catch {
    return false;
  }
};

export const generateUserId = (address) => {
  if (!address) return 'AD000000';
  const hash = address.slice(-6).toUpperCase();
  return `AD${hash}`;
};

// Get random avatar based on address (consistent for same address)
export const getRandomAvatar = (address) => {
  if (!address) return '/u1.gif';
  const num = (parseInt(address.slice(-2), 16) % 10) + 1;
  return `/u${num}.gif`;
};

// Get random NFT background based on NFT ID (consistent for same ID)
export const getRandomNFTBg = (nftId) => {
  if (!nftId) return '/n1.gif';
  const num = (parseInt(nftId) % 12) + 1;
  return `/n${num}.gif`;
};

// Activity type labels - Updated for new contract
export const getActivityLabel = (type) => {
  const labels = {
    'REG': '📝 Registered',
    'PUR': '🛒 Purchased NFTs',
    'SOLD': '💰 NFT Sold',
    'SPO': '🎯 Sponsor Income',
    'LVL': '📊 Level Income',
    'PRF': '💎 Profit Received',
    'PRI': '💵 Principal Returned',
    'PND': '⏳ Pending Reinvest',
    'REP': '🔄 Repurchased',
    'REF': '↩️ Refunded',
    'CAP': '🏦 To Capital',
    'BRN': '🔥 NFT Burned',
    'WA': '💳 Withdrawn',
    // Legacy labels
    'Registration': '📝 Registered',
    'Purchase': '🛒 Purchased NFTs',
    'NFT_Sold': '💰 NFT Sold',
    'Sponsor': '🎯 Sponsor Income',
    'Level': '📊 Level Income',
    'Profit': '💎 Profit Received',
    'Principal': '💵 Principal Returned',
    'Pending': '⏳ Pending Reinvest',
    'Repurchase': '🔄 Repurchased',
    'Refund': '↩️ Refunded',
    'Capital': '🏦 To Capital',
    'NFT_Burned': '🔥 NFT Burned'
  };
  return labels[type] || `📌 ${type}`;
};
